<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";
import { useRouter } from "vue-router";

const router = useRouter();

// Dummy project data — would be loaded by ID from route params in production
const project = {
  id: 'P-2024-0031',
  name: 'Solar Park Beta – Northern Highlands',
  sector: 'Renewable Energy',
  country: 'Kenya',
  region: 'Rift Valley',
  methodology: 'ACM0002 v19.0',
  doe: 'Bureau Veritas Carbon Assurance',
  status: 'Active',
  statusColor: 'green',
  registrationDate: '2024-03-15',
  creditingPeriodStart: '2024-04-01',
  creditingPeriodEnd: '2029-03-31',
  estimatedAnnualCredits: 12500,
  totalCreditsIssued: 6120,
  creditsAvailable: 5840,
  creditsRetired: 280,
  creditsInTransfer: 0,
}

const timeline = [
  { date: '2024-02-01', event: 'PCN Submitted', status: 'complete' },
  { date: '2024-02-20', event: 'LoA Received from DNA', status: 'complete' },
  { date: '2024-02-28', event: 'PIN Submitted', status: 'complete' },
  { date: '2024-03-05', event: 'PDD Submitted', status: 'complete' },
  { date: '2024-03-15', event: 'DOE Validation Complete', status: 'complete' },
  { date: '2024-03-15', event: 'Project Registered (CR-2024-00089)', status: 'complete' },
  { date: '2024-04-01', event: 'Crediting Period Commenced', status: 'complete' },
  { date: '2024-11-20', event: 'Verification Period 1 Approved – 6,120 tCO2e', status: 'complete' },
  { date: '2025-04-08', event: 'Monitoring Reports Period 2 Submitted', status: 'active' },
  { date: '2025-06-30', event: 'Crediting Period Renewal Due', status: 'upcoming' },
]

const documents = [
  { name: 'PCN Form Document', date: '2024-02-01', status: 'Approved' },
  { name: 'Project Identification Note (PIN)', date: '2024-02-28', status: 'Approved' },
  { name: 'Project Design Document (PDD)', date: '2024-03-05', status: 'Approved' },
  { name: 'DOE Validation Report', date: '2024-03-14', status: 'Approved' },
  { name: 'Registration Certificate (CR-2024-00089)', date: '2024-03-15', status: 'Issued' },
  { name: 'Monitoring Report – Period 1', date: '2024-10-05', status: 'Verified' },
  { name: 'Verification Report – Period 1', date: '2024-11-20', status: 'Approved' },
  { name: 'Monitoring Report – Period 2', date: '2025-04-08', status: 'Under Review' },
]

const docStatusClass = (s: string) => {
  const map: Record<string, string> = {
    'Approved': 'bg-green-100 text-green-800',
    'Verified': 'bg-green-100 text-green-800',
    'Issued': 'bg-blue-100 text-blue-800',
    'Under Review': 'bg-yellow-100 text-yellow-800',
    'Rejected': 'bg-red-100 text-red-800',
  }
  return map[s] ?? 'bg-gray-100 text-gray-600'
}
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />

    <main class="flex-1 overflow-auto py-8">
      <div class="max-w-6xl mx-auto px-5">

        <!-- Breadcrumb -->
        <nav class="text-sm text-gray-500 mb-4">
          <span class="hover:text-primary cursor-pointer" @click="router.push('/developer/projects')">My Projects</span>
          <span class="mx-2">/</span>
          <span class="text-gray-800 font-medium">{{ project.name }}</span>
        </nav>

        <!-- Header -->
        <section class="mb-6">
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <div class="flex items-start justify-between">
              <div>
                <div class="flex items-center space-x-3 mb-1">
                  <h1 class="text-2xl font-bold text-accent">{{ project.name }}</h1>
                  <span class="bg-green-100 text-green-800 inline-flex px-2 py-1 rounded-full text-xs font-medium">
                    {{ project.status }}
                  </span>
                </div>
                <p class="text-sm text-gray-500">
                  {{ project.id }} &nbsp;·&nbsp; {{ project.sector }} &nbsp;·&nbsp; {{ project.country }}, {{ project.region }}
                </p>
              </div>
              <div class="flex items-center space-x-2">
                <button @click="router.push('/developer/project/amend')" class="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50">
                  <i class="fas fa-edit mr-1"></i>Amend
                </button>
                <button @click="router.push('/developer/upload-reports')" class="px-4 py-2 bg-primary text-white rounded-md text-sm hover:bg-primary/90">
                  <i class="fas fa-chart-line mr-1"></i>Upload Reports
                </button>
              </div>
            </div>
          </div>
        </section>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

          <!-- Left: main info -->
          <div class="lg:col-span-2 space-y-6">

            <!-- Credit summary -->
            <section class="bg-white rounded-lg border border-gray-200 p-6">
              <h2 class="text-lg font-semibold text-accent mb-4">Credit Summary</h2>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div class="bg-primary/5 rounded-lg p-3">
                  <p class="text-xl font-bold text-primary">{{ project.totalCreditsIssued.toLocaleString() }}</p>
                  <p class="text-xs text-gray-600">Total Issued (tCO2e)</p>
                </div>
                <div class="bg-green-50 rounded-lg p-3">
                  <p class="text-xl font-bold text-green-600">{{ project.creditsAvailable.toLocaleString() }}</p>
                  <p class="text-xs text-gray-600">Available</p>
                </div>
                <div class="bg-gray-50 rounded-lg p-3">
                  <p class="text-xl font-bold text-gray-600">{{ project.creditsRetired.toLocaleString() }}</p>
                  <p class="text-xs text-gray-600">Retired</p>
                </div>
                <div class="bg-blue-50 rounded-lg p-3">
                  <p class="text-xl font-bold text-blue-600">{{ project.estimatedAnnualCredits.toLocaleString() }}</p>
                  <p class="text-xs text-gray-600">Est. Annual (tCO2e)</p>
                </div>
              </div>
              <div class="flex items-center space-x-3 mt-4">
                <button @click="router.push('/developer/request-credits')" class="px-4 py-1.5 bg-primary text-white rounded-md text-sm hover:bg-primary/90">
                  <i class="fas fa-certificate mr-1"></i>Request Issuance
                </button>
                <button @click="router.push('/developer/retire-credits')" class="px-4 py-1.5 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50">
                  <i class="fas fa-fire mr-1"></i>Retire Credits
                </button>
                <button @click="router.push('/developer/transfer-credits')" class="px-4 py-1.5 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50">
                  <i class="fas fa-exchange-alt mr-1"></i>Transfer
                </button>
              </div>
            </section>

            <!-- Project details -->
            <section class="bg-white rounded-lg border border-gray-200 p-6">
              <h2 class="text-lg font-semibold text-accent mb-4">Project Details</h2>
              <div class="grid grid-cols-2 gap-4 text-sm">
                <div><p class="text-gray-500">Methodology</p><p class="font-medium text-gray-800">{{ project.methodology }}</p></div>
                <div><p class="text-gray-500">DOE</p><p class="font-medium text-gray-800">{{ project.doe }}</p></div>
                <div><p class="text-gray-500">Registration Date</p><p class="font-medium text-gray-800">{{ project.registrationDate }}</p></div>
                <div><p class="text-gray-500">Crediting Period</p><p class="font-medium text-gray-800">{{ project.creditingPeriodStart }} – {{ project.creditingPeriodEnd }}</p></div>
              </div>
            </section>

            <!-- Documents -->
            <section class="bg-white rounded-lg border border-gray-200 p-6">
              <h2 class="text-lg font-semibold text-accent mb-4">Documents</h2>
              <div class="divide-y divide-gray-100">
                <div v-for="doc in documents" :key="doc.name" class="flex items-center justify-between py-3">
                  <div class="flex items-center space-x-3">
                    <i class="fas fa-file-pdf text-red-400"></i>
                    <div>
                      <p class="text-sm font-medium text-gray-800">{{ doc.name }}</p>
                      <p class="text-xs text-gray-500">{{ doc.date }}</p>
                    </div>
                  </div>
                  <div class="flex items-center space-x-3">
                    <span :class="['inline-flex px-2 py-0.5 rounded-full text-xs font-medium', docStatusClass(doc.status)]">
                      {{ doc.status }}
                    </span>
                    <button class="text-primary text-sm hover:underline">
                      <i class="fas fa-download"></i>
                    </button>
                  </div>
                </div>
              </div>
            </section>

          </div>

          <!-- Right: timeline + quick actions -->
          <div class="space-y-6">

            <!-- Quick actions -->
            <section class="bg-white rounded-lg border border-gray-200 p-5">
              <h2 class="text-sm font-semibold text-accent mb-3">Quick Actions</h2>
              <div class="space-y-2">
                <button @click="router.push('/developer/project/verification-status')" class="w-full text-left flex items-center space-x-3 px-3 py-2 rounded-md hover:bg-gray-50 text-sm text-gray-700">
                  <i class="fas fa-clipboard-check text-primary w-4"></i>
                  <span>View Verification Status</span>
                </button>
                <button @click="router.push('/developer/project/loa-status')" class="w-full text-left flex items-center space-x-3 px-3 py-2 rounded-md hover:bg-gray-50 text-sm text-gray-700">
                  <i class="fas fa-stamp text-primary w-4"></i>
                  <span>LoA Status</span>
                </button>
                <button @click="router.push('/developer/project/registration-certificate')" class="w-full text-left flex items-center space-x-3 px-3 py-2 rounded-md hover:bg-gray-50 text-sm text-gray-700">
                  <i class="fas fa-award text-primary w-4"></i>
                  <span>Registration Certificate</span>
                </button>
                <button @click="router.push('/developer/project/sdg-reporting')" class="w-full text-left flex items-center space-x-3 px-3 py-2 rounded-md hover:bg-gray-50 text-sm text-gray-700">
                  <i class="fas fa-globe text-primary w-4"></i>
                  <span>SDG Co-benefits Report</span>
                </button>
                <button @click="router.push('/developer/project/renew-crediting-period')" class="w-full text-left flex items-center space-x-3 px-3 py-2 rounded-md hover:bg-gray-50 text-sm text-gray-700">
                  <i class="fas fa-redo text-primary w-4"></i>
                  <span>Renew Crediting Period</span>
                </button>
                <button @click="router.push('/developer/project/report-reversal')" class="w-full text-left flex items-center space-x-3 px-3 py-2 rounded-md hover:bg-gray-50 text-sm text-gray-700">
                  <i class="fas fa-exclamation-triangle text-error w-4"></i>
                  <span>Report Reversal</span>
                </button>
              </div>
            </section>

            <!-- Timeline -->
            <section class="bg-white rounded-lg border border-gray-200 p-5">
              <h2 class="text-sm font-semibold text-accent mb-4">Project Timeline</h2>
              <div class="space-y-3">
                <div v-for="item in timeline" :key="item.date + item.event" class="flex items-start space-x-3">
                  <div :class="[
                    'w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0',
                    item.status === 'complete' ? 'bg-green-500' :
                    item.status === 'active' ? 'bg-primary' :
                    'bg-gray-300'
                  ]"></div>
                  <div>
                    <p class="text-xs text-gray-500">{{ item.date }}</p>
                    <p :class="['text-sm', item.status === 'upcoming' ? 'text-gray-400' : 'text-gray-700']">{{ item.event }}</p>
                  </div>
                </div>
              </div>
            </section>

          </div>
        </div>

      </div>
    </main>
  </div>
</template>
